import { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Student Progress Dashboard - YISRAEL LEARNING CENTER
 * 
 * Shows comprehensive progress tracking:
 * - Overall progress percentage
 * - Progress by grade level (K-12)
 * - Seminary progress
 * - Live Sky Test status
 * - Next recommended action
 */

function StudentProgress({ user }) {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanding, setExpanding] = useState(null);

  useEffect(() => {
    loadProgress();
  }, []);

  const loadProgress = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/progress/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProgress(response.data);
    } catch (err) {
      console.error("Error loading progress:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>📊</div>
        <p style={{ color: "#888" }}>Loading your progress...</p>
      </div>
    );
  }

  if (!progress) {
    return (
      <div className="card" style={{ textAlign: "center", padding: "40px" }}>
        <div style={{ fontSize: "2rem", marginBottom: "15px" }}>📊</div>
        <p style={{ color: "#888" }}>Unable to load progress. Please try again.</p>
      </div>
    );
  }

  const overall = progress.overall_progress || {};
  const gradeProgress = progress.grade_progress || {};
  const seminaryProgress = progress.seminary_progress;
  const liveSkyStatus = progress.live_sky_test_status || {};

  return (
    <div data-testid="student-progress">
      {/* Overall Progress Card */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <h2 style={{ color: "#FFD700", marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}>
          <span>📊</span> Your Progress
        </h2>

        {/* Big Progress Circle */}
        <div style={{ 
          display: "flex", 
          justifyContent: "center", 
          alignItems: "center",
          marginBottom: "25px"
        }}>
          <div style={{
            position: "relative",
            width: "180px",
            height: "180px"
          }}>
            {/* Background circle */}
            <svg width="180" height="180" style={{ transform: "rotate(-90deg)" }}>
              <circle
                cx="90"
                cy="90"
                r="80"
                fill="none"
                stroke="#222"
                strokeWidth="12"
              />
              {/* Progress arc */}
              <circle
                cx="90"
                cy="90"
                r="80"
                fill="none"
                stroke="url(#progressGradient)"
                strokeWidth="12"
                strokeLinecap="round"
                strokeDasharray={`${(overall.percentage || 0) * 5.03} 503`}
              />
              <defs>
                <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#FFD700" />
                  <stop offset="100%" stopColor="#90EE90" />
                </linearGradient>
              </defs>
            </svg>
            {/* Percentage text */}
            <div style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              textAlign: "center"
            }}>
              <div style={{ 
                color: "#FFD700", 
                fontSize: "2.5rem", 
                fontWeight: "bold",
                lineHeight: 1
              }}>
                {overall.percentage?.toFixed(0) || 0}%
              </div>
              <div style={{ color: "#888", fontSize: "0.9rem", marginTop: "5px" }}>
                Complete
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div style={{ 
          display: "grid", 
          gridTemplateColumns: "repeat(3, 1fr)", 
          gap: "15px",
          marginBottom: "20px"
        }}>
          <div style={{ 
            background: "#1a1a2a", 
            padding: "15px", 
            borderRadius: "10px",
            textAlign: "center"
          }}>
            <div style={{ color: "#FFD700", fontSize: "1.8rem", fontWeight: "bold" }}>
              {overall.completed_lessons || 0}
            </div>
            <div style={{ color: "#888", fontSize: "0.8rem" }}>Lessons Done</div>
          </div>
          <div style={{ 
            background: "#1a1a2a", 
            padding: "15px", 
            borderRadius: "10px",
            textAlign: "center"
          }}>
            <div style={{ color: "#90EE90", fontSize: "1.8rem", fontWeight: "bold" }}>
              {overall.grades_completed || 0}
            </div>
            <div style={{ color: "#888", fontSize: "0.8rem" }}>Grades Done</div>
          </div>
          <div style={{ 
            background: "#1a1a2a", 
            padding: "15px", 
            borderRadius: "10px",
            textAlign: "center"
          }}>
            <div style={{ color: "#87CEEB", fontSize: "1.8rem", fontWeight: "bold" }}>
              {overall.current_grade || "K"}
            </div>
            <div style={{ color: "#888", fontSize: "0.8rem" }}>Current Grade</div>
          </div>
        </div>

        {/* Next Action */}
        {progress.next_action && (
          <div style={{
            background: "linear-gradient(135deg, #1a2a1a 0%, #0a1a0a 100%)",
            border: "1px solid #90EE90",
            padding: "15px",
            borderRadius: "10px"
          }}>
            <div style={{ color: "#90EE90", fontWeight: "bold", marginBottom: "5px" }}>
              📌 Recommended Next Step
            </div>
            <div style={{ color: "#eee" }}>{progress.next_action}</div>
          </div>
        )}
      </div>

      {/* Grade Progress Grid */}
      <div className="card" style={{ marginBottom: "20px" }}>
        <h3 style={{ color: "#87CEEB", marginBottom: "15px" }}>
          📚 Progress by Grade (K-12)
        </h3>
        
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))", gap: "10px" }}>
          {["K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12"].map(grade => {
            const gp = gradeProgress[grade] || {};
            const isComplete = gp.grade_complete;
            const isCurrent = overall.current_grade === grade;
            const needsSkyTest = gp.requires_live_sky_test && !isComplete;
            
            return (
              <div
                key={grade}
                onClick={() => setExpanding(expanding === grade ? null : grade)}
                style={{
                  background: isComplete 
                    ? "linear-gradient(135deg, #1a3a1a 0%, #0a2a0a 100%)"
                    : isCurrent
                    ? "linear-gradient(135deg, #2a2a1a 0%, #1a1a0a 100%)"
                    : "#1a1a2a",
                  border: isComplete 
                    ? "2px solid #90EE90"
                    : isCurrent
                    ? "2px solid #FFD700"
                    : "1px solid #333",
                  borderRadius: "10px",
                  padding: "12px",
                  textAlign: "center",
                  cursor: "pointer",
                  transition: "all 0.2s"
                }}
              >
                <div style={{ 
                  fontSize: "1.2rem", 
                  fontWeight: "bold",
                  color: isComplete ? "#90EE90" : isCurrent ? "#FFD700" : "#888"
                }}>
                  {grade === "K" ? "K" : grade}
                </div>
                <div style={{ 
                  fontSize: "0.8rem", 
                  color: isComplete ? "#90EE90" : "#888",
                  marginTop: "5px"
                }}>
                  {gp.percentage?.toFixed(0) || 0}%
                </div>
                {needsSkyTest && (
                  <div style={{ fontSize: "0.7rem", color: "#FFD700", marginTop: "3px" }}>
                    🔭
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Expanded Grade Details */}
        {expanding && gradeProgress[expanding] && (
          <div style={{
            marginTop: "15px",
            padding: "15px",
            background: "#1a1a2a",
            borderRadius: "10px",
            border: "1px solid #333"
          }}>
            <h4 style={{ color: "#FFD700", margin: "0 0 10px 0" }}>
              Grade {expanding} Details
            </h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
              <div>
                <div style={{ color: "#888", fontSize: "0.8rem" }}>Lessons Completed</div>
                <div style={{ color: "#eee", fontWeight: "bold" }}>
                  {gradeProgress[expanding].completed_lessons} / {gradeProgress[expanding].total_lessons}
                </div>
              </div>
              <div>
                <div style={{ color: "#888", fontSize: "0.8rem" }}>Exams Passed</div>
                <div style={{ color: "#eee", fontWeight: "bold" }}>
                  {gradeProgress[expanding].exams_passed}
                </div>
              </div>
            </div>
            {gradeProgress[expanding].requires_live_sky_test && (
              <div style={{
                marginTop: "10px",
                padding: "10px",
                background: "#2a2a1a",
                borderRadius: "8px",
                border: "1px solid #FFD700"
              }}>
                <div style={{ color: "#FFD700", fontSize: "0.9rem" }}>
                  🔭 Live Sky Test Required
                </div>
                <div style={{ color: "#888", fontSize: "0.8rem" }}>
                  Pass the observatory verification to advance
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Live Sky Test Status */}
      {liveSkyStatus.required_for_current_grade && (
        <div className="card" style={{ marginBottom: "20px" }}>
          <h3 style={{ color: "#FFD700", marginBottom: "15px" }}>
            🔭 Live Sky Test Status
          </h3>
          <div style={{
            background: "#1a1a2a",
            padding: "15px",
            borderRadius: "10px"
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ color: "#eee", fontWeight: "bold" }}>
                  Observatory Verification
                </div>
                <div style={{ color: "#888", fontSize: "0.9rem" }}>
                  Tests Passed: {liveSkyStatus.tests_passed || 0}
                </div>
              </div>
              <div style={{
                padding: "8px 15px",
                borderRadius: "20px",
                background: liveSkyStatus.tests_passed > 0 ? "#1a3a1a" : "#3a1a1a",
                color: liveSkyStatus.tests_passed > 0 ? "#90EE90" : "#ff6b6b",
                fontWeight: "bold"
              }}>
                {liveSkyStatus.tests_passed > 0 ? "In Progress" : "Required"}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seminary Progress */}
      {seminaryProgress && (
        <div className="card">
          <h3 style={{ color: "#DDA0DD", marginBottom: "15px" }}>
            🎓 Seminary Progress
          </h3>
          <div style={{
            background: "#2a1a2a",
            padding: "20px",
            borderRadius: "12px",
            border: "1px solid #DDA0DD"
          }}>
            <div style={{ 
              display: "flex", 
              justifyContent: "space-between", 
              alignItems: "center",
              marginBottom: "15px"
            }}>
              <div>
                <div style={{ color: "#DDA0DD", fontWeight: "bold", textTransform: "capitalize" }}>
                  {seminaryProgress.degree} Degree Track
                </div>
                <div style={{ color: "#888", fontSize: "0.9rem" }}>
                  {seminaryProgress.courses_completed} courses completed
                </div>
              </div>
              <div style={{
                background: "#1a1a2a",
                padding: "10px 20px",
                borderRadius: "20px",
                color: "#FFD700",
                fontWeight: "bold"
              }}>
                Active
              </div>
            </div>
            
            {seminaryProgress.requires_live_sky_test && (
              <div style={{
                padding: "10px",
                background: "#1a1a2a",
                borderRadius: "8px",
                border: "1px solid #FFD700"
              }}>
                <div style={{ color: "#FFD700", fontSize: "0.9rem" }}>
                  🔭 Advanced Live Sky Tests Required
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default StudentProgress;
